import { useState, useRef, useEffect } from "react";
import { prepare, layout } from "@chenglou/pretext";

interface ExpandableDescriptionProps {
  text: string;
  maxLines?: number;
  className?: string;
  textClassName?: string;
  font?: string;
  lineHeight?: number;
}

const getLineClampClass = (lines: number) => {
  switch (lines) {
    case 1: return "line-clamp-1";
    case 2: return "line-clamp-2";
    case 3: return "line-clamp-3";
    case 4: return "line-clamp-4";
    case 5: return "line-clamp-5";
    default: return "line-clamp-3";
  }
};

export function ExpandableDescription({
  text,
  maxLines = 3,
  className = "",
  textClassName = "text-sm text-foreground/70",
  font = "14px Poppins, Montserrat, sans-serif",
  lineHeight = 22.75,
}: ExpandableDescriptionProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [shouldShowToggle, setShouldShowToggle] = useState(false);
  const containerRef = useRef<HTMLParagraphElement>(null);
  const [width, setWidth] = useState(0);

  // Monitor element resize to re-calculate pretext layout dynamically
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const observer = new ResizeObserver((entries) => {
      if (!entries || entries.length === 0) return;
      const entry = entries[0];
      if (!entry) return;
      const contentWidth = entry.contentRect.width;
      if (contentWidth > 0) {
        setWidth(contentWidth);
      }
    });

    observer.observe(el);
    // Initial width grab
    const rect = el.getBoundingClientRect();
    if (rect.width > 0) {
      setWidth(rect.width);
    }

    return () => observer.disconnect();
  }, []);

  // Compute text line count via @chenglou/pretext when text or width changes
  useEffect(() => {
    if (!text || width === 0) return;

    try {
      const prepared = prepare(text, font);

      // Calculate layout using pretext (lineHeight value doesn't affect lineCount arithmetic)
      const res = layout(prepared, width, lineHeight);

      setShouldShowToggle(res.lineCount > maxLines);
    } catch (e) {
      console.warn("Pretext layout calculation failed, falling back to CSS line clamp", e);
      // Fallback
      setShouldShowToggle(text.length > 150);
    }
  }, [text, width, maxLines, font, lineHeight]);

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card onClick modal trigger
    setIsExpanded((prev) => !prev);
  };

  const activeLineClamp = getLineClampClass(maxLines);

  return (
    <div className={`space-y-2 ${className}`}>
      <p
        ref={containerRef}
        className={`${textClassName} leading-relaxed transition-all duration-300 ${isExpanded ? "" : activeLineClamp
          }`}
      >
        {text}
      </p>

      {shouldShowToggle && (
        <button
          onClick={handleToggle}
          type="button"
          className="text-xs font-extrabold text-brand-primary hover:text-brand-primary/80 transition-colors uppercase tracking-wider flex items-center gap-1.5 focus:outline-none"
        >
          {isExpanded ? (
            <>
              <span>Show Less</span>
              <span className="text-[10px]">↑</span>
            </>
          ) : (
            <>
              <span>Read More</span>
              <span className="text-[10px]">↓</span>
            </>
          )}
        </button>
      )}
    </div>
  );
}

export default ExpandableDescription;
